/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `ant_tx` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class AntTxMesg : Mesg {
    public constructor() : super("AntTx", Profile.MesgNum.ANT_TX)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** fractional_timestamp, in s. */
    public var fractionalTimestamp: Double?
        get() = getFieldValue(FRACTIONAL_TIMESTAMP_FIELD_NUM) as? Double
        set(value) = setFieldValue(FRACTIONAL_TIMESTAMP_FIELD_NUM, value)

    /** mesg_id. */
    public var mesgId: UByte?
        get() = getFieldValue(MESG_ID_FIELD_NUM) as? UByte
        set(value) = setFieldValue(MESG_ID_FIELD_NUM, value)

    /** Number of values stored in mesg_data. */
    public val numMesgData: Int
        get() = getField(MESG_DATA_FIELD_NUM)?.numValues ?: 0

    /** mesg_data. */
    public var mesgData: List<UByte?>?
        get() {
            val field = getField(MESG_DATA_FIELD_NUM) ?: return null
            return List(field.numValues) { getMesgData(it) }
        }
        set(value) {
            removeField(MESG_DATA_FIELD_NUM)
            value?.forEachIndexed { index, element -> setMesgData(index, element) }
        }

    public fun getMesgData(index: Int): UByte? =
        getFieldValue(MESG_DATA_FIELD_NUM, index) as? UByte

    public fun setMesgData(index: Int, value: UByte?) {
        setFieldValue(MESG_DATA_FIELD_NUM, value, index)
    }

    /** channel_number. */
    public var channelNumber: UByte?
        get() = getFieldValue(CHANNEL_NUMBER_FIELD_NUM) as? UByte
        set(value) = setFieldValue(CHANNEL_NUMBER_FIELD_NUM, value)

    /** Number of values stored in data. */
    public val numData: Int
        get() = getField(DATA_FIELD_NUM)?.numValues ?: 0

    /** data. */
    public var data: List<UByte?>?
        get() {
            val field = getField(DATA_FIELD_NUM) ?: return null
            return List(field.numValues) { getData(it) }
        }
        set(value) {
            removeField(DATA_FIELD_NUM)
            value?.forEachIndexed { index, element -> setData(index, element) }
        }

    public fun getData(index: Int): UByte? =
        getFieldValue(DATA_FIELD_NUM, index) as? UByte

    public fun setData(index: Int, value: UByte?) {
        setFieldValue(DATA_FIELD_NUM, value, index)
    }

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val FRACTIONAL_TIMESTAMP_FIELD_NUM: UByte = 0u
        public const val MESG_ID_FIELD_NUM: UByte = 1u
        public const val MESG_DATA_FIELD_NUM: UByte = 2u
        public const val CHANNEL_NUMBER_FIELD_NUM: UByte = 3u
        public const val DATA_FIELD_NUM: UByte = 4u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("FractionalTimestamp", 0u, BaseType.UINT16, 32768.0, 0.0, "s", false)
            fields[1] = Field("MesgId", 1u, BaseType.BYTE, 1.0, 0.0, "", false)
            fields[2] = Field("MesgData", 2u, BaseType.BYTE, 1.0, 0.0, "", false)
                .addComponent(FieldComponent(3u, false, 8, 1.0, 0.0))
                .addComponent(FieldComponent(4u, false, 8, 1.0, 0.0))
                .addComponent(FieldComponent(4u, false, 8, 1.0, 0.0))
                .addComponent(FieldComponent(4u, false, 8, 1.0, 0.0))
                .addComponent(FieldComponent(4u, false, 8, 1.0, 0.0))
                .addComponent(FieldComponent(4u, false, 8, 1.0, 0.0))
                .addComponent(FieldComponent(4u, false, 8, 1.0, 0.0))
                .addComponent(FieldComponent(4u, false, 8, 1.0, 0.0))
                .addComponent(FieldComponent(4u, false, 8, 1.0, 0.0))
            fields[3] = Field("ChannelNumber", 3u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[4] = Field("Data", 4u, BaseType.BYTE, 1.0, 0.0, "", false)
            return fields
        }
    }
}
